import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, Sparkles, RotateCcw } from 'lucide-react';

const PROPERTY_TYPES = [
  { code: 'duplex', key: 'duplex' },
  { code: 'triplex', key: 'triplex' },
  { code: 'fourplex', key: 'fourplex' },
  { code: 'five_plus', key: 'fivePlus' },
  { code: 'apartment_building', key: 'apartmentBuilding' },
  { code: 'mixed_use', key: 'mixedUse' },
  { code: 'other', key: 'other' },
];
const RENOVATION = ['turnkey', 'light', 'value_add', 'major', 'brrrr'];
const STRATEGIES = ['cash_flow', 'appreciation', 'brrrr', 'value_add', 'long_term_hold', 'portfolio_growth'];
const RISK = ['conservative', 'moderate', 'aggressive'];

const empty = {
  use_investor_profile: false,
  location: '',
  property_types: [],
  min_units: '',
  max_units: '',
  min_price: '',
  max_price: '',
  down_payment_amount: '',
  down_payment_percent: '',
  min_monthly_cash_flow: '',
  min_cap_rate: '',
  min_cash_on_cash: '',
  renovation_preference: '',
  strategy: '',
  risk_tolerance: '',
};

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border transition ${
        active
          ? 'bg-[#12355B] dark:bg-[#45C04A] text-white dark:text-[#12355B] border-transparent'
          : 'bg-white dark:bg-[#131824] text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-[#45C04A]'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Deal Scanner criteria form.
 * Emits a normalized criteria dict via onScan().
 */
export default function CriteriaForm({
  initial,
  hasProfile,
  onScan,
  onSaveSearch,
  scanning,
  canSaveSearch,
  saveSearchTooltip,
}) {
  const { t } = useTranslation();
  const [c, setC] = useState({ ...empty, ...(initial || {}) });

  const set = (k, v) => setC((prev) => ({ ...prev, [k]: v }));
  const toggleType = (code) => {
    setC((prev) => {
      const cur = prev.property_types || [];
      return {
        ...prev,
        property_types: cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code],
      };
    });
  };
  const reset = () => setC({ ...empty });

  const buildCriteria = () => ({
    use_investor_profile: !!c.use_investor_profile,
    location: (c.location || '').trim() || null,
    property_types: c.property_types || [],
    min_units: numOrNull(c.min_units),
    max_units: numOrNull(c.max_units),
    min_price: numOrNull(c.min_price),
    max_price: numOrNull(c.max_price),
    down_payment_amount: numOrNull(c.down_payment_amount),
    down_payment_percent: numOrNull(c.down_payment_percent),
    min_monthly_cash_flow: numOrNull(c.min_monthly_cash_flow),
    min_cap_rate: numOrNull(c.min_cap_rate),
    min_cash_on_cash: numOrNull(c.min_cash_on_cash),
    renovation_preference: c.renovation_preference || null,
    strategy: c.strategy || null,
    risk_tolerance: c.risk_tolerance || null,
  });

  const handleScan = () => onScan(buildCriteria());
  const handleSave = () => {
    const name = window.prompt(t('dealScanner.saveSearchPromptName'));
    if (!name || !name.trim()) return;
    onSaveSearch(name.trim(), buildCriteria());
  };

  return (
    <div
      data-testid="scanner-criteria-form"
      className="bg-white dark:bg-[#131824] rounded-2xl border border-slate-200 dark:border-slate-800 p-6 space-y-6"
    >
      {/* Use Investor Profile */}
      <div className="flex items-start justify-between gap-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#45C04A] mt-0.5" />
          <div>
            <div className="font-semibold text-[#12355B] dark:text-white">
              {t('dealScanner.useProfile')}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-md">
              {hasProfile
                ? t('dealScanner.useProfileDesc')
                : t('dealScanner.noProfileDesc')}
            </p>
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            data-testid="scanner-use-profile-toggle"
            className="sr-only peer"
            checked={!!c.use_investor_profile}
            disabled={!hasProfile}
            onChange={(e) => set('use_investor_profile', e.target.checked)}
          />
          <div className="w-11 h-6 bg-slate-300 dark:bg-slate-700 peer-checked:bg-[#45C04A] rounded-full peer-disabled:opacity-40 relative transition">
            <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5" />
          </div>
        </label>
      </div>

      {/* Location */}
      <div>
        <Label className="text-[#12355B] dark:text-white font-semibold">
          {t('dealScanner.location')}
        </Label>
        <Input
          data-testid="scanner-location-input"
          value={c.location}
          onChange={(e) => set('location', e.target.value)}
          placeholder={t('dealScanner.locationPlaceholder')}
          className="mt-1"
        />
        <p className="text-[11px] text-slate-500 mt-1">{t('dealScanner.locationHint')}</p>
      </div>

      {/* Property Type */}
      <div>
        <Label className="text-[#12355B] dark:text-white font-semibold">
          {t('dealScanner.propertyType')}
        </Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PROPERTY_TYPES.map((pt) => (
            <Chip
              key={pt.code}
              active={c.property_types.includes(pt.code)}
              onClick={() => toggleType(pt.code)}
              testId={`scanner-type-${pt.code}`}
            >
              {t(`dealScanner.types.${pt.key}`)}
            </Chip>
          ))}
        </div>
      </div>

      {/* Price / Units grids */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('dealScanner.minPrice')}</Label>
          <Input
            data-testid="scanner-min-price"
            type="number" min="0" placeholder="0"
            value={c.min_price} onChange={(e) => set('min_price', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.maxPrice')}</Label>
          <Input
            data-testid="scanner-max-price"
            type="number" min="0" placeholder="0"
            value={c.max_price} onChange={(e) => set('max_price', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.minUnits')}</Label>
          <Input
            data-testid="scanner-min-units"
            type="number" min="0" placeholder="0"
            value={c.min_units} onChange={(e) => set('min_units', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.maxUnits')}</Label>
          <Input
            data-testid="scanner-max-units"
            type="number" min="0" placeholder="0"
            value={c.max_units} onChange={(e) => set('max_units', e.target.value)}
          />
        </div>
      </div>

      {/* Down payment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('dealScanner.downPaymentAmount')}</Label>
          <Input
            data-testid="scanner-dp-amount"
            type="number" min="0"
            value={c.down_payment_amount} onChange={(e) => set('down_payment_amount', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.downPaymentPercent')}</Label>
          <Input
            data-testid="scanner-dp-percent"
            type="number" min="0" max="100"
            value={c.down_payment_percent} onChange={(e) => set('down_payment_percent', e.target.value)}
          />
        </div>
      </div>

      {/* Investment metrics targets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>{t('dealScanner.minCashFlow')}</Label>
          <Input
            data-testid="scanner-min-cash-flow"
            type="number" placeholder="500"
            value={c.min_monthly_cash_flow} onChange={(e) => set('min_monthly_cash_flow', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.minCapRate')}</Label>
          <Input
            data-testid="scanner-min-cap-rate"
            type="number" step="0.1" placeholder="5.5"
            value={c.min_cap_rate} onChange={(e) => set('min_cap_rate', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('dealScanner.minCoc')}</Label>
          <Input
            data-testid="scanner-min-coc"
            type="number" step="0.1" placeholder="6"
            value={c.min_cash_on_cash} onChange={(e) => set('min_cash_on_cash', e.target.value)}
          />
        </div>
      </div>

      {/* Renovation preference */}
      <div>
        <Label className="text-[#12355B] dark:text-white font-semibold">{t('dealScanner.renovation')}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {RENOVATION.map((r) => (
            <Chip
              key={r}
              active={c.renovation_preference === r}
              onClick={() => set('renovation_preference', c.renovation_preference === r ? '' : r)}
              testId={`scanner-renov-${r}`}
            >
              {t(`dealScanner.renovOpts.${r}`)}
            </Chip>
          ))}
        </div>
      </div>

      {/* Strategy */}
      <div>
        <Label className="text-[#12355B] dark:text-white font-semibold">{t('dealScanner.strategy')}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {STRATEGIES.map((s) => (
            <Chip
              key={s}
              active={c.strategy === s}
              onClick={() => set('strategy', c.strategy === s ? '' : s)}
              testId={`scanner-strat-${s}`}
            >
              {t(`dealScanner.stratOpts.${s}`)}
            </Chip>
          ))}
        </div>
      </div>

      {/* Risk */}
      <div>
        <Label className="text-[#12355B] dark:text-white font-semibold">{t('dealScanner.risk')}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {RISK.map((r) => (
            <Chip
              key={r}
              active={c.risk_tolerance === r}
              onClick={() => set('risk_tolerance', c.risk_tolerance === r ? '' : r)}
              testId={`scanner-risk-${r}`}
            >
              {t(`dealScanner.riskOpts.${r}`)}
            </Chip>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
        <Button
          data-testid="scanner-scan-button"
          onClick={handleScan}
          disabled={scanning}
          className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-6 py-3 font-semibold"
        >
          <Search className="w-5 h-5 mr-2" />
          {scanning ? t('dealScanner.scanning') : t('dealScanner.scanCta')}
        </Button>
        <Button
          data-testid="scanner-save-search-button"
          onClick={handleSave}
          disabled={!canSaveSearch}
          title={!canSaveSearch ? saveSearchTooltip : ''}
          variant="outline"
          className="rounded-xl px-6 py-3 font-semibold border-slate-300 dark:border-slate-700"
        >
          {t('dealScanner.saveSearch')}
        </Button>
        <Button
          data-testid="scanner-reset-button"
          onClick={reset}
          variant="ghost"
          className="rounded-xl px-4 py-3 text-slate-600 dark:text-slate-300"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {t('dealScanner.resetCriteria')}
        </Button>
      </div>
    </div>
  );
}
